import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { History, ExternalLink } from "lucide-react";

const AUDIT_META = {
  mark_invalid: { color: "bg-red-50 text-red-800 border-red-300", label: "Marked invalid" },
  reinstate: { color: "bg-emerald-50 text-emerald-800 border-emerald-300", label: "Reinstated" },
  note: { color: "bg-slate-100 text-slate-700 border-slate-300", label: "Note" },
};

export default function AdminAudit() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");

  const load = () => {
    setLoading(true);
    const q = action === "all" ? "" : `?action=${action}`;
    api.get(`/admin/audit${q}`)
      .then((r) => setRows(r.data.items))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (user?.role === "admin") load(); /* eslint-disable-next-line */ }, [user?.role, action]);

  if (user && user.role !== "admin") {
    return <div className="max-w-3xl mx-auto p-10"><Card className="p-8 text-center">Admin only.</Card></div>;
  }

  const filters = [
    { key: "all", label: "All" },
    { key: "mark_invalid", label: "Invalidations" },
    { key: "reinstate", label: "Reinstatements" },
    { key: "note", label: "Notes" },
  ];

  return (
    <div className="max-w-5xl mx-auto px-4 py-10 space-y-6" data-testid="admin-audit-page">
      <div className="flex items-center gap-3">
        <History className="w-8 h-8 text-[#6B1D2F]" />
        <div>
          <h1 className="font-serif text-3xl font-semibold text-slate-900">Audit log</h1>
          <p className="text-slate-600">Every admin action, in reverse chronological order.</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        {filters.map((f) => (
          <Button
            key={f.key}
            data-testid={`audit-filter-${f.key}`}
            variant={action === f.key ? "default" : "outline"}
            className={action === f.key ? "bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full" : "rounded-full"}
            onClick={() => setAction(f.key)}
          >
            {f.label}
          </Button>
        ))}
      </div>

      {loading ? (
        <Card className="p-10 text-center text-slate-500 border-[#EAE3D2] bg-white">Loading…</Card>
      ) : rows.length === 0 ? (
        <Card className="p-10 text-center text-slate-500 border-[#EAE3D2] bg-white" data-testid="audit-empty">No actions yet.</Card>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const meta = AUDIT_META[r.action] || { color: "bg-slate-100 text-slate-700 border-slate-300", label: r.action };
            return (
              <Card key={r.id} className="p-4 border-[#EAE3D2] bg-white" data-testid={`audit-row-${r.id}`}>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className={meta.color}>{meta.label}</Badge>
                  {r.reason_code && <Badge className="bg-[#6B1D2F]/10 text-[#6B1D2F] border-[#6B1D2F]/30">{r.reason_code}</Badge>}
                  <span className="text-sm text-slate-800">
                    <b>{r.admin_name}</b>{" "}
                    on{" "}
                    <b>{r.target?.full_name || r.target_id?.slice(0, 8)}</b>
                  </span>
                  <span className="text-xs text-slate-400 ml-auto">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                {r.reason_text && <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap"><span className="text-slate-500 text-xs">Reason:</span> {r.reason_text}</p>}
                {r.note && <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap"><span className="text-slate-500 text-xs">Note:</span> {r.note}</p>}
                <div className="mt-2">
                  <Link to={`/admin/members?open=${r.target_id}`} className="text-xs text-[#6B1D2F] inline-flex items-center gap-1">
                    Open member <ExternalLink className="w-3 h-3" />
                  </Link>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
